import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { addPersonFilter, removePersonFilter, addPlaceFilter, removePlaceFilter, addYearFilter, removeYearFilter, addEventFilter, removeEventFilter, setFilter, clearFilters } from '../../../slice/filterSlice';
import { useLanguage } from '../../../contexts/LanguageContext';
import { createLogger } from '../../../utils/logger';
import FilterChipList from './FilterChipList';

const logger = createLogger('Sidebar');

interface SidebarProps {
  isOpen: boolean;
  onClose?: () => void;
}

export default function Sidebar({ isOpen, onClose }: SidebarProps) {
  const dispatch = useDispatch();
  const { t } = useLanguage();
  const filters = useSelector((state: RootState) => state.filters);
  const tags = useSelector((state: RootState) => state.tags);
  const [searchQuery, setSearchQuery] = useState('');

   // Debug: Log when component renders
   logger.debug('RENDER] filters.people=', filters.people, 'filters.places=', filters.places);

   // Helper function to filter tags by search query
   const filterBySearch = (items: string[]): string[] => {
     if (!searchQuery.trim()) return items;
     const lowerQuery = searchQuery.toLowerCase();
     return items.filter(item => item.toLowerCase().includes(lowerQuery));
   };

   // Memoize derived tag lists (alphabetically sorted, recomputed only when tags change)
   const people = useMemo(
     () =>
       filterBySearch(
         Array.from(
           new Set(
             tags.allIds
               .map(id => tags.byId[id])
               .filter(tag => tag.type === 'person')
               .map(tag => tag.name)
           )
         )
       ),
     [tags.allIds, tags.byId, searchQuery]
   );

   const places = useMemo(
     () =>
       filterBySearch(
         Array.from(
           new Set(
             tags.allIds
               .map(id => tags.byId[id])
               .filter(tag => tag.type === 'place')
               .map(tag => tag.name)
           )
         )
       ),
     [tags.allIds, tags.byId, searchQuery]
   );

   const years = useMemo(
     () =>
       filterBySearch(
         Array.from(
           new Set(
             tags.allIds
               .map(id => tags.byId[id])
               .filter(tag => tag.type === 'year')
               .map(tag => tag.name)
           )
         )
       ),
     [tags.allIds, tags.byId, searchQuery]
   );

   const events = useMemo(
     () =>
       filterBySearch(
         Array.from(
           new Set(
             tags.allIds
               .map(id => tags.byId[id])
               .filter(tag => tag.type === 'event')
               .map(tag => tag.name)
           )
         )
       ),
     [tags.allIds, tags.byId, searchQuery]
   );

   const handlePersonClick = (e: React.MouseEvent, person: string) => {
     e.stopPropagation(); // Prevent sidebar overlay from closing
     logger.debug('Person clicked=', person, 'Current people=', filters.people);
     if (filters.people.includes(person)) {
       logger.debug('Removing person filter');
       dispatch(removePersonFilter(person));
     } else {
       logger.debug('Adding person filter');
       dispatch(addPersonFilter(person));
     }
     // Log Redux state after dispatch
     setTimeout(() => {
       logger.debug('After dispatch, people=', filters.people);
     }, 100);
     // Close sidebar on mobile after filter selection
     onClose?.();
   };

   const handlePlaceClick = (e: React.MouseEvent, place: string) => {
     e.stopPropagation(); // Prevent sidebar overlay from closing
     logger.debug('Place clicked=', place, 'Current places=', filters.places);
     if (filters.places.includes(place)) {
       logger.debug('Removing place filter');
       dispatch(removePlaceFilter(place));
     } else {
       logger.debug('Adding place filter');
       dispatch(addPlaceFilter(place));
     }
     // Close sidebar on mobile after filter selection
     onClose?.();
   };

   const handleYearClick = (e: React.MouseEvent, year: string) => {
     e.stopPropagation(); // Prevent sidebar overlay from closing
     logger.debug('Year clicked=', year, 'Current years=', filters.years);
     if (filters.years.includes(year)) {
       logger.debug('Removing year filter');
       dispatch(removeYearFilter(year));
     } else {
       logger.debug('Adding year filter');
       dispatch(addYearFilter(year));
     }
     // Close sidebar on mobile after filter selection
     onClose?.();
   };

   const handleEventClick = (e: React.MouseEvent, event: string) => {
     e.stopPropagation(); // Prevent sidebar overlay from closing
     logger.debug('Event clicked=', event, 'Current events=', filters.events);
     if (filters.events.includes(event)) {
       logger.debug('Removing event filter');
       dispatch(removeEventFilter(event));
     } else {
       logger.debug('Adding event filter');
       dispatch(addEventFilter(event));
     }
     // Close sidebar on mobile after filter selection
     onClose?.();
   };

   const handleVisibilityClick = (e: React.MouseEvent, visibility: 'all' | 'public' | 'private') => {
     e.stopPropagation(); // Prevent sidebar overlay from closing
     dispatch(setFilter({ visibility }));
     // Close sidebar on mobile after filter selection
     onClose?.();
   };

  const handleResetFilters = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(clearFilters());
  };

  // Check if any filters are active
  const hasActiveFilters = filters.people.length > 0 || filters.places.length > 0 || filters.years.length > 0 || filters.events.length > 0 || filters.visibility !== 'all';

  return (
    <div 
      className={`sidebar ${isOpen ? 'active' : ''}`}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Search Input */}
      <div style={{ marginBottom: '1.5rem' }}>
        <input
          type="text"
          placeholder={t('sidebar.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="sidebar-search-input"
          aria-label={t('sidebar.searchPlaceholder')}
        />
      </div>

      {/* Reset All Filters Button */}
       {hasActiveFilters && (
         <div style={{ marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '1px solid var(--border-color)' }}>
           <button
             className="reset-filters-btn"
             onClick={handleResetFilters}
           >
             ✕ {t('sidebar.resetAllFilters')}
           </button>
         </div>
       )}

        {/* People */}
        <div className="filter-section">
          <div className="filter-title">{t('sidebar.people')}</div>
          <FilterChipList
            items={people}
            activeItems={filters.people}
            onItemClick={handlePersonClick}
            emptyLabel={t('sidebar.noPeopleTagged')}
            forceExpanded={searchQuery.length > 0}
            noMatchLabel={t('sidebar.noMatchesFound')}
          />
        </div>

        {/* Places */}
        <div className="filter-section">
          <div className="filter-title">{t('sidebar.places')}</div>
          <FilterChipList
            items={places}
            activeItems={filters.places}
            onItemClick={handlePlaceClick}
            emptyLabel={t('sidebar.noPlacesTagged')}
            forceExpanded={searchQuery.length > 0}
            noMatchLabel={t('sidebar.noMatchesFound')}
          />
        </div>

        {/* Years */}
        <div className="filter-section">
          <div className="filter-title">{t('sidebar.years')}</div>
          <FilterChipList
            items={years}
            activeItems={filters.years}
            onItemClick={handleYearClick}
            emptyLabel={t('sidebar.noYearsTagged')}
            forceExpanded={searchQuery.length > 0}
            noMatchLabel={t('sidebar.noMatchesFound')}
          />
        </div>

        {/* Events */}
        <div className="filter-section">
          <div className="filter-title">{t('sidebar.events')}</div>
          <FilterChipList
            items={events}
            activeItems={filters.events}
            onItemClick={handleEventClick}
            emptyLabel={t('sidebar.noEventsTagged')}
            forceExpanded={searchQuery.length > 0}
            noMatchLabel={t('sidebar.noMatchesFound')}
          />
        </div>

       {/* Visibility */}
       <div className="filter-section">
         <div className="filter-title">{t('sidebar.visibility')}</div>
         <div>
           <span
             className={`filter-chip ${filters.visibility === 'all' ? 'active' : ''}`}
             onClick={(e) => handleVisibilityClick(e, 'all')}
           >
             {t('sidebar.all')}
           </span>
           <span
             className={`filter-chip ${filters.visibility === 'public' ? 'active' : ''}`}
             onClick={(e) => handleVisibilityClick(e, 'public')}
           >
             {t('sidebar.public')}
           </span>
           <span
             className={`filter-chip ${filters.visibility === 'private' ? 'active' : ''}`}
             onClick={(e) => handleVisibilityClick(e, 'private')}
           >
             {t('sidebar.private')}
           </span>
         </div>
       </div>
    </div>
  );
}
